import type { Appointment, AvailabilitySlot, AppointmentType } from '../mocks/appointments';

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function getTimeSlots(startTime: string, endTime: string): string[] {
  const slots: string[] = [];
  let m = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  while (m < end) {
    slots.push(minutesToTime(m));
    m += 30;
  }
  return slots;
}

export interface TimeSlotData {
  time: string;
  capacity: number;
  booked: number;
  available: number;
}

export interface DayOccupancy {
  totalCapacity: number;
  bookedCapacity: number;
  availableCapacity: number;
  occupancyPct: number;
  timeSlots: TimeSlotData[];
}

export function calculateDayOccupancy(
  dateISO: string,
  dow: number,
  availabilitySlots: AvailabilitySlot[],
  appointments: Appointment[],
  types: AppointmentType[],
): DayOccupancy {
  const activeSlots = availabilitySlots.filter((s) => s.day_of_week === dow && s.is_active);

  if (activeSlots.length === 0) {
    return {
      totalCapacity: 0,
      bookedCapacity: 0,
      availableCapacity: 0,
      occupancyPct: 0,
      timeSlots: [],
    };
  }

  const timeSet = new Set<string>();
  activeSlots.forEach((slot) => {
    getTimeSlots(slot.start_time, slot.end_time).forEach((t) => timeSet.add(t));
  });

  const sortedTimes = Array.from(timeSet).sort();

  const timeSlots: TimeSlotData[] = sortedTimes.map((time) => {
    const covering = activeSlots.filter((s) => time >= s.start_time && time < s.end_time);
    const capacity = covering.reduce((sum, s) => sum + s.max_bookings, 0);
    return { time, capacity, booked: 0, available: capacity };
  });

  const dayAppts = appointments.filter(
    (a) => a.preferred_date === dateISO && a.status !== 'cancelled',
  );

  dayAppts.forEach((appt) => {
    if (!appt.preferred_time) return;
    const type = types.find((t) => t.name === appt.service_type);
    const duration = type?.duration_minutes || 30;
    const apptStart = timeToMinutes(appt.preferred_time);
    const apptEnd = apptStart + duration;

    timeSlots.forEach((slot) => {
      const slotStart = timeToMinutes(slot.time);
      const slotEnd = slotStart + 30;
      if (apptStart < slotEnd && apptEnd > slotStart) {
        slot.booked += 1;
      }
    });
  });

  timeSlots.forEach((slot) => {
    slot.available = Math.max(0, slot.capacity - slot.booked);
  });

  const totalCapacity = timeSlots.reduce((sum, s) => sum + s.capacity, 0);
  const bookedCapacity = timeSlots.reduce((sum, s) => sum + s.booked, 0);
  const availableCapacity = Math.max(0, totalCapacity - bookedCapacity);
  const occupancyPct = totalCapacity > 0 ? Math.round((bookedCapacity / totalCapacity) * 100) : 0;

  return {
    totalCapacity,
    bookedCapacity,
    availableCapacity,
    occupancyPct,
    timeSlots,
  };
}

export function getSlotUsageWithDuration(
  slot: AvailabilitySlot,
  appointments: Appointment[],
  types: AppointmentType[],
): number {
  const usageByDate = new Map<string, number>();

  appointments
    .filter((a) => a.status !== 'cancelled')
    .forEach((appt) => {
      const d = new Date(appt.preferred_date);
      if (d.getDay() !== slot.day_of_week) return;
      if (!appt.preferred_time) return;

      const type = types.find((t) => t.name === appt.service_type);
      const duration = type?.duration_minutes || 30;
      const apptStart = timeToMinutes(appt.preferred_time);
      const apptEnd = apptStart + duration;
      const slotStart = timeToMinutes(slot.start_time);
      const slotEnd = timeToMinutes(slot.end_time);

      if (apptStart < slotEnd && apptEnd > slotStart) {
        usageByDate.set(appt.preferred_date, (usageByDate.get(appt.preferred_date) || 0) + 1);
      }
    });

  return usageByDate.size > 0 ? Math.max(...Array.from(usageByDate.values())) : 0;
}

export interface FranjaOccupancy {
  start: string;
  end: string;
  maxBookings: number;
  booked: number;
  available: number;
  pct: number;
}

export function calculateFranjaOccupancy(
  dateISO: string,
  dow: number,
  availabilitySlots: AvailabilitySlot[],
  appointments: Appointment[],
  types: AppointmentType[],
): FranjaOccupancy[] {
  const activeSlots = availabilitySlots.filter((s) => s.day_of_week === dow && s.is_active);
  if (activeSlots.length === 0) return [];

  const dayAppts = appointments.filter(
    (a) => a.preferred_date === dateISO && a.status !== 'cancelled',
  );

  return activeSlots
    .map((slot) => {
      let booked = 0;
      dayAppts.forEach((appt) => {
        if (!appt.preferred_time) return;
        const type = types.find((t) => t.name === appt.service_type);
        const duration = type?.duration_minutes || 30;
        const apptStart = timeToMinutes(appt.preferred_time);
        const apptEnd = apptStart + duration;
        const slotStart = timeToMinutes(slot.start_time);
        const slotEnd = timeToMinutes(slot.end_time);
        if (apptStart < slotEnd && apptEnd > slotStart) {
          booked += 1;
        }
      });
      const available = Math.max(0, slot.max_bookings - booked);
      const pct = slot.max_bookings > 0 ? Math.round((booked / slot.max_bookings) * 100) : 0;
      return {
        start: slot.start_time,
        end: slot.end_time,
        maxBookings: slot.max_bookings,
        booked,
        available,
        pct,
      };
    })
    .sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
}

export function getActiveAppointmentsInSlot(
  slot: AvailabilitySlot,
  appointments: Appointment[],
  types: AppointmentType[],
): number {
  return appointments.filter((appt) => {
    if (appt.status === 'cancelled') return false;
    if (!appt.preferred_time) return false;
    const d = new Date(appt.preferred_date);
    if (d.getDay() !== slot.day_of_week) return false;

    const type = types.find((t) => t.name === appt.service_type);
    const duration = type?.duration_minutes || 30;
    const apptStart = timeToMinutes(appt.preferred_time);
    const apptEnd = apptStart + duration;
    const slotStart = timeToMinutes(slot.start_time);
    const slotEnd = timeToMinutes(slot.end_time);

    return apptStart < slotEnd && apptEnd > slotStart;
  }).length;
}